// BK-3229 R4 / Decision 2: fill {contact.X} / {account.X} placeholders in
// Library template bodies at template-click time, using the substitutions
// tree returned from POST /ai/compose_context (Rails ContextComposer).
//
// Substitution happens before setInputText so the user sees the resolved
// text in the Compose textarea and can review/edit before sending. Picked
// over server-side substitution specifically for that visibility.
//
// Missing values (key absent or null/empty in the substitutions tree) leave
// the literal {contact.X} token in the output and surface in `missingTokens`
// so the caller can log telemetry. The user sees the gap and can edit it
// out — beats emitting an empty sentence or a fake "[N/A]" placeholder.
//
// Performance envelope:
//   Template bodies in v1.yaml are 0.5–3 KB with 1–10 token occurrences.
//   At those sizes the regex sweep is sub-millisecond (~50–200µs measured
//   locally) and well below the 16ms render budget. If templates ever cross
//   ~50 KB or token counts cross ~200, profile before optimising — likely
//   wins are: cache the regex match positions, or pre-compile per-template
//   substitution functions. Today neither is needed.

const NS_TOKEN_RE = /\{(contact|account)\.([\w.]+)\}/g;
const LAST_AI_MSG_RE = /\{last_ai_message\}/g;

const resolvePath = (root, path) =>
  path.split(".").reduce((value, key) => {
    if (value == null || typeof value !== "object") {
      return undefined;
    }
    return value[key];
  }, root);

const isPresent = (value) =>
  value != null && (typeof value !== "string" || value.length > 0);

const substituteTemplatePlaceholders = (
  body,
  substitutions = {},
  lastAiMessage = "",
) => {
  if (typeof body !== "string" || body.length === 0) {
    return { text: body ?? "", missingTokens: [] };
  }

  const missing = [];

  const text = body
    .replace(NS_TOKEN_RE, (match, ns, path) => {
      const value = resolvePath(substitutions[ns], path);
      if (!isPresent(value)) {
        missing.push(match);
        return match;
      }
      return String(value);
    })
    .replace(LAST_AI_MSG_RE, () => lastAiMessage || "");

  return { text, missingTokens: missing };
};

export default substituteTemplatePlaceholders;
